import type { User } from 'firebase/auth';
import { createContext, useContext } from 'react';
import type { PartnerUserType } from '@/types/types_partnerUser';

// Presence of `admins/{uid}` grants CMS access. The collection name lives in
// types_admin.ts, which the Cloud Functions import too, so both halves of the
// app check the same place.

export interface AuthContextValue {
  /** The signed-in Firebase user, or null when signed out. */
  user: User | null;
  /** True when the signed-in user has an `admins/{uid}` doc. */
  isAdmin: boolean;
  /**
   * The signed-in user's partner account, or null. Created by redeeming an
   * invitation (see `src/types/types_invite.ts`). The UI reads this doc; the
   * matching custom claims are what the security rules check.
   */
  partnerUser: PartnerUserType | null;
  /** True for an *active* partner account — a disabled one does not count. */
  isPartner: boolean;
  /** True while auth state (and, for a signed-in user, their roles) is resolving. */
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Creates an email/password account and sets its display name. */
  createAccountWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOutUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
